import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  isNum, fx, fmtN, rankText, DeltaRank, DeltaPct, parseIso, isoOf, ymd, md, dayLabel, meanR, sumR, movingAverage, pct, niceMax
} from './analytics/format';

// MEO分析の比較分析。依頼者の画面案のとおり、左と右に同じグラフを並べて見比べる。
//
// グラフの形は固定で、変えられるのは左右の「比べる対象」と「期間」だけ。
// 対象は 店舗・契約会社・業種・地域・全店舗（立地はデータが無いので出していない）。
// 右だけ、店舗以外のときに「比べる対象2」で契約会社・業種・地域をもう1つ重ねられる（両方に当てはまる店）。
// まとめた対象は「1店舗あたりの1日の値」で、店舗1つと同じものさしで比べられる。
//
// 左右の値はそれぞれ compare_series から読む（AnalyticsController#compare_series）。
// 対象か期間が変わったほうだけ読み直す。
//
// 縦軸をそろえる（初期値 ON）と、UU数・アクション・対策の件数の目盛りを左右で大きいほうに合わせる。
// 順位はもともと 1位〜圏外 の同じ目盛り。

const SIDE_COLOR = { L: '#1c7ed6', R: '#e8590c' };
const KW1_COLOR = '#0d366b';
const AVG_COLOR = '#495057';
const ACTION_PARTS = [['call', '電話', '#343a40'], ['route', '経路検索', '#6c757d'], ['site', 'サイト', '#adb5bd']];

const addDays = (iso, n) => { const d = parseIso(iso); d.setUTCDate(d.getUTCDate() + n); return isoOf(d); };
const daysBetween = (a, b) => Math.round((parseIso(b) - parseIso(a)) / 86400000);
const clampIso = (iso, min, max) => (iso < min ? min : iso > max ? max : iso);

export default function StoreCompare(props) {
  const { base, options, kinds, measureKinds, outOfRank, urls, startDate, endDate, defaultDays } = props;
  const defFrom = addDays(endDate, -(defaultDays - 1));

  const idOf = (kind) => {
    const first = (options[kind] || [])[0];
    const pick = { store: base.id, company: base.clientId, industry: base.industryId, pref: base.prefId, all: '' }[kind];
    if (kind === 'all') return '';
    return pick !== null && pick !== undefined ? pick : first ? first.id : '';
  };
  const target = (kind, id, from = defFrom, to = endDate) => ({ kind, id: id === undefined ? idOf(kind) : id, kind2: '', id2: '', from, to });

  const [L, setL] = useState(target('store'));
  const [R, setR] = useState(target(base.industryId ? 'industry' : 'all'));
  const [syncY, setSyncY] = useState(true);
  const [syncP, setSyncP] = useState(true);
  const [loaded, setLoaded] = useState({});   // 読み込みの鍵 => { status, data, reason }
  const [toast, setToast] = useState(null);

  const right = syncP ? { ...R, from: L.from, to: L.to } : R;
  // 店舗1つのときは2つ目の条件を使わない
  const cond2 = (t) => (t.kind === 'store' || !t.kind2 ? { kind2: '', id2: '' } : { kind2: t.kind2, id2: t.id2 });
  const keyOf = (t) => { const c = cond2(t); return `${t.kind}|${t.id}|${c.kind2}|${c.id2}|${t.from}|${t.to}`; };

  const load = (t) => {
    const key = keyOf(t);
    if (loaded[key]) return;
    setLoaded((m) => ({ ...m, [key]: { status: 'loading' } }));
    const c = cond2(t);
    const q = `kind=${t.kind}&id=${encodeURIComponent(t.id)}&kind2=${c.kind2}&id2=${encodeURIComponent(c.id2)}&from=${t.from}&to=${t.to}`;
    fetch(`${urls.series}?${q}`, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((res) => res.json())
      .then((json) => setLoaded((m) => ({ ...m, [key]: json.available ? { status: 'ok', data: json } : { status: 'na', reason: json.reason } })))
      .catch(() => setLoaded((m) => ({ ...m, [key]: { status: 'na', reason: '読み込めませんでした' } })));
  };
  useEffect(() => { load(L); }, [keyOf(L)]);
  useEffect(() => { load(right); }, [keyOf(right)]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  const left = useMemo(() => sideData(L, loaded[keyOf(L)]), [loaded, keyOf(L)]);
  const rightData = useMemo(() => sideData(right, loaded[keyOf(right)]), [loaded, keyOf(right)]);

  // 縦軸。そろえるときは左右の大きいほう
  const scaleOf = (d) => {
    const pickMax = (k) => (syncY ? Math.max(left.max[k], rightData.max[k]) : d.max[k]);
    return { uu: niceMax(pickMax('uu') * 1.08), week: niceMax(pickMax('week') * 1.08), count: Math.max(1, niceMax(pickMax('count'))) };
  };

  // ---- よく使う比べ方 ----
  const yoy = () => {
    const from = clampIso(addDays(L.from, -365), startDate, endDate);
    const to = clampIso(addDays(L.to, -365), startDate, endDate);
    if (daysBetween(startDate, addDays(L.from, -365)) < 0) setToast(`前年のデータは${ymd(startDate)}からです。そこから後だけを出します`);
    setL(target('store', base.id, L.from, L.to));
    setR(target('store', base.id, from, to));
    setSyncP(false);
  };
  const presets = [
    base.industryId && { label: 'この店舗 と 同業種の平均', fn: () => { setL(target('store', base.id, L.from, L.to)); setR(target('industry', base.industryId, L.from, L.to)); setSyncP(true); } },
    { label: 'この店舗の 今年 と 昨年', fn: yoy },
    base.siblings.length > 0 && { label: '同じ契約会社の店舗どうし', fn: () => { setL(target('store', base.id, L.from, L.to)); setR(target('store', base.siblings[0], L.from, L.to)); setSyncP(true); } },
    base.prefId && { label: 'この店舗 と 同じ地域の平均', fn: () => { setL(target('store', base.id, L.from, L.to)); setR(target('pref', base.prefId, L.from, L.to)); setSyncP(true); } },
    { label: 'この店舗 と 全店舗の平均', fn: () => { setL(target('store', base.id, L.from, L.to)); setR(target('all', '', L.from, L.to)); setSyncP(true); } }
  ].filter(Boolean);

  // 左には比べる対象2が無いので、右から移すときは外す
  const swap = () => { const l = L; setL({ ...right, kind2: '', id2: '' }); setR(l); setSyncP(false); };

  const csvHref = `${urls.csv}?${[['l', L], ['r', right]].map(([s, t]) => {
    const c = cond2(t);
    return `${s}_kind=${t.kind}&${s}_id=${encodeURIComponent(t.id)}&${s}_kind2=${c.kind2}&${s}_id2=${encodeURIComponent(c.id2)}&${s}_from=${t.from}&${s}_to=${t.to}`;
  }).join('&')}`;

  // 比べる対象を切り替えたときに初めから選ぶもの。左で選んでいる店舗の会社・業種・地域にする
  // （左が店舗でないときや読み込み中は、この画面を開いた店舗）。
  const refs = L.kind === 'store' && left.json.refs
    ? { store: L.id, company: left.json.refs.clientId, industry: left.json.refs.industryId, pref: left.json.refs.prefId }
    : { store: base.id, company: base.clientId, industry: base.industryId, pref: base.prefId };

  const markerRow = [left, rightData].some((x) => x.json.markers.length > 0);
  const sideProps = { refs, markerRow, options, kinds, subKinds: props.subKinds, measureKinds, outOfRank, urls, startDate, endDate, setToast };

  return (
    <div className="sd">
      <div className="sc-presets">
        <span className="sd-note">よく使う比べ方：</span>
        {presets.map((p) => <button key={p.label} type="button" className="sd-btn sd-small sd-ghost" onClick={p.fn}>{p.label}</button>)}
        <span className="sc-sep" />
        <label className="sc-check"><input type="checkbox" checked={syncY} onChange={(e) => setSyncY(e.target.checked)} /> 縦軸をそろえる</label>
        <label className="sc-check"><input type="checkbox" checked={syncP} onChange={(e) => setSyncP(e.target.checked)} /> 右の期間を左に合わせる</label>
        <button type="button" className="sd-btn sd-small" onClick={swap}>⇄ 左右を入れ替え</button>
        <a className="sd-btn sd-small" href={csvHref}>⤓ CSV</a>
      </div>

      <div className="sc-grid">
        <Side side="L" t={L} onChange={setL} state={loaded[keyOf(L)]} d={left} scale={scaleOf(left)} other={null} {...sideProps} />
        <Side side="R" t={right} onChange={(t) => setR(t)} dateLocked={syncP} state={loaded[keyOf(right)]} d={rightData}
              scale={scaleOf(rightData)} other={left} {...sideProps} />
      </div>

      {toast && <div className="sd-toast" role="status">{toast}</div>}
    </div>
  );
}

// 読み込む前・読めなかったときの値。グラフの枠と軸だけを描くのに使う
const EMPTY_JSON = { uu: {}, act: {}, call: {}, route: {}, site: {}, kw1: {}, kwavg: {}, counts: {}, markers: [], stores: 1 };

// 読み込んだ値を、期間の日ごとの配列にする。
// 読み込み中・読めなかったときも空の値で作る（左右でグラフの形と高さをそろえるため）。
function sideData(t, state) {
  const json = state && state.status === 'ok' ? state.data : EMPTY_JSON;
  const dates = [];
  for (let d = t.from; d <= t.to; d = addDays(d, 1)) dates.push(d);
  const arr = (map) => dates.map((d) => (map && isNum(map[d]) ? map[d] : null));
  const s = {
    uu: arr(json.uu), act: arr(json.act), call: arr(json.call), route: arr(json.route), site: arr(json.site),
    kw1: arr(json.kw1), kwavg: arr(json.kwavg)
  };
  const n = dates.length - 1;
  const ma = { uu: movingAverage(s.uu), kw1: movingAverage(s.kw1), kwavg: movingAverage(s.kwavg) };

  // 週ごとの合計（期間の初日から7日ずつ）
  const weeks = [];
  for (let a = 0; a <= n; a += 7) {
    const b = Math.min(a + 6, n);
    const w = { a, b };
    ACTION_PARTS.forEach(([k]) => { w[k] = sumR(s[k], a, b) || 0; });
    w.total = w.call + w.route + w.site;
    weeks.push(w);
  }

  // アクション率は、UU数とアクション数の両方がある日で数える
  let uuSum = 0; let actSum = 0;
  s.uu.forEach((v, i) => { if (isNum(v) && isNum(s.act[i])) { uuSum += v; actSum += s.act[i]; } });

  const kpi = {
    kw1: meanR(s.kw1, 0, n), avg: meanR(s.kwavg, 0, n), uu: meanR(s.uu, 0, n), act: meanR(s.act, 0, n),
    rate: uuSum ? (actSum / uuSum) * 100 : null
  };
  const counts = json.counts || {};
  return {
    json, dates, s, ma, weeks, kpi, counts,
    hasRank: s.kwavg.some(isNum),
    max: {
      uu: Math.max(1, ...ma.uu.filter(isNum)),
      week: Math.max(1, ...weeks.map((w) => w.total)),
      count: Math.max(0, ...Object.values(counts))
    }
  };
}

// ---- 左右の1つぶん ----------------------------------------------------------

function Side({ side, t, onChange, dateLocked, state, d, scale, other, refs, markerRow, options, kinds, subKinds, measureKinds, outOfRank, urls, startDate, endDate, setToast }) {
  const color = SIDE_COLOR[side];
  const json = state && state.status === 'ok' ? d.json : null;
  // グラフの中に出す文字（読み込み中・読めなかった理由）。データが無いだけのときはグラフごとに出す
  const msg = !state || state.status === 'loading' ? '読み込み中…' : state.status === 'na' ? state.reason : null;

  // 対象を切り替えたときに初めから選ぶもの（左の店舗の会社・業種・地域。無ければ候補の先頭）
  const initialId = (kind) => {
    if (kind === 'all' || !kind) return '';
    const opts = options[kind] || [];
    const ref = refs[kind];
    return ref !== null && ref !== undefined && ref !== '' ? ref : opts[0] ? opts[0].id : '';
  };
  // 店舗にしたら2つ目の条件は外す
  const onKind = (kind) => onChange({ ...t, kind, id: initialId(kind), ...(kind === 'store' ? { kind2: '', id2: '' } : {}) });
  const onKind2 = (kind2) => onChange({ ...t, kind2, id2: initialId(kind2) });
  const onStore = (value) => {
    const v = value.trim();
    if (!v) return;
    const code = (v.match(/A\d+/i) || [])[0];
    const hit = options.store.find((o) => o.label === v)
      || (code && options.store.find((o) => o.label.split(' ')[0].toUpperCase() === code.toUpperCase()))
      || options.store.find((o) => o.label.includes(v));
    if (hit) onChange({ ...t, id: hit.id });
    else setToast('店舗が見つかりません。店舗名かAコード（例：A00003368）で探してください');
  };
  const onDate = (field, value) => {
    if (!value) return;
    const v = clampIso(value, startDate, endDate);
    if (field === 'from') onChange({ ...t, from: v, to: t.to < v ? v : t.to });
    else onChange({ ...t, to: v < t.from ? t.from : v });
  };

  // 直近にデータの無い店は候補に無いので、読み込んだ名前を出す
  const storeLabel = t.kind === 'store' ? (options.store.find((o) => String(o.id) === String(t.id)) || {}).label || (json ? json.title : '') : '';

  return (
    <div className="sc-side">
      <div className="sc-head" style={{ '--sc': color }}>
        <div className="sc-side-name">{side === 'L' ? '左' : '右'}</div>
        <div className="sc-title">
          {json ? json.title : state && state.status === 'na' ? '—' : '読み込み中…'}
          {json && <span className="sd-note">{json.sub}</span>}
          {state && state.status === 'na' && <span className="sd-note">{state.reason}</span>}
          {json && t.kind === 'store' && <a className="sc-link" href={`${urls.dashboard}${t.id}`}>店舗ダッシュボード →</a>}
        </div>
        {/* 右は3段（比べる対象と名前／比べる対象2と名前2／期間）、左は2段。左の枠は右と同じ高さで、余りは期間の下に空ける */}
        <div className="sc-sel">
          <span className="sc-lab">比べる対象</span>
          <select value={t.kind} onChange={(e) => onKind(e.target.value)}>
            {kinds.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
          </select>
          {t.kind === 'store' && (
            <>
              <span className="sc-lab">店舗</span>
              <input key={`${side}-${t.id}-${storeLabel}`} type="text" list="sc-store-list" placeholder="店舗名・Aコードで検索"
                     defaultValue={storeLabel}
                     onKeyDown={(e) => { if (e.key === 'Enter') onStore(e.target.value); }}
                     onChange={(e) => { if (options.store.some((o) => o.label === e.target.value)) onStore(e.target.value); }} />
            </>
          )}
          {t.kind !== 'store' && t.kind !== 'all' && (
            <>
              <span className="sc-lab">名前</span>
              <select value={String(t.id)} onChange={(e) => onChange({ ...t, id: e.target.value })}>
                {(options[t.kind] || []).map((o) => <option key={o.id} value={String(o.id)}>{o.label}</option>)}
              </select>
            </>
          )}
          {t.kind === 'all' && <span className="sc-span2" />}

          {side === 'R' && (
            <>
              <span className="sc-lab">比べる対象2</span>
              {t.kind === 'store' ? (
                <span className="sc-span3 sd-note">店舗のときは使いません</span>
              ) : (
                <>
                  <select value={t.kind2} onChange={(e) => onKind2(e.target.value)}>
                    <option value="">なし</option>
                    {kinds.filter((k) => subKinds.includes(k.key)).map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
                  </select>
                  {t.kind2 ? (
                    <>
                      <span className="sc-lab">名前2</span>
                      <select value={String(t.id2)} onChange={(e) => onChange({ ...t, id2: e.target.value })}>
                        {(options[t.kind2] || []).map((o) => <option key={o.id} value={String(o.id)}>{o.label}</option>)}
                      </select>
                    </>
                  ) : <span className="sc-span2" />}
                </>
              )}
            </>
          )}

          <span className="sc-lab">期間</span>
          <span className="sc-span3 sc-dates">
            <input type="date" value={t.from} min={startDate} max={endDate} disabled={dateLocked} onChange={(e) => onDate('from', e.target.value)} aria-label="開始日" />
            〜
            <input type="date" value={t.to} min={startDate} max={endDate} disabled={dateLocked} onChange={(e) => onDate('to', e.target.value)} aria-label="終了日" />
            {dateLocked && <span className="sd-note">左と同じ</span>}
          </span>
        </div>
        {side === 'L' && (
          <datalist id="sc-store-list">
            {options.store.map((o) => <option key={o.id} value={o.label} />)}
          </datalist>
        )}
      </div>

      {/* .sc-body は display:contents。KPI と4つのグラフが .sc-grid の段に1つずつ乗り、左右で高さがそろう */}
      <div className="sc-body">
        <Kpis d={d} other={other} outOfRank={outOfRank} />
        <div className="sd-card">
          <header><h2>順位の推移</h2><span className="sd-sub">7日移動平均 ・ 1位が上{d.json.kw1Name ? ` ・ KW1＝${d.json.kw1Name}` : ''}</span></header>
          <div className="sd-legend"><span><i className="sd-lg-line" style={{ background: KW1_COLOR }} />KW1</span><span><i className="sd-lg-line" style={{ background: AVG_COLOR }} />平均順位</span></div>
          <div className="sd-body"><RankChart d={d} outOfRank={outOfRank} msg={msg || (!d.hasRank && 'この期間の順位のデータがありません')} /></div>
        </div>
        <div className="sd-card">
          <header><h2>UU数の推移</h2><span className="sd-sub">7日移動平均{d.json.stores > 1 ? ' ・ 1店舗あたり' : ''}{d.json.markers.length ? ' ・ 下の丸は対策' : ''}</span></header>
          <div className="sd-body"><UuChart d={d} max={scale.uu} measureKinds={measureKinds} markerRow={markerRow} msg={msg || (!d.s.uu.some(isNum) && 'この期間のUU数のデータがありません')} /></div>
        </div>
        <div className="sd-card">
          <header><h2>アクションの内訳（週ごと）</h2><span className="sd-sub">{d.json.stores > 1 ? '1店舗あたりの週の合計' : '週の合計'}</span></header>
          <div className="sd-legend">{ACTION_PARTS.map(([k, label, c]) => <span key={k}><i style={{ width: 10, height: 10, background: c }} />{label}</span>)}</div>
          <div className="sd-body"><WeekChart d={d} max={scale.week} msg={msg || (!d.s.act.some(isNum) && 'この期間のアクションのデータがありません')} /></div>
        </div>
        <div className="sd-card">
          <header><h2>対策の件数</h2><span className="sd-sub">{d.json.stores > 1 ? `期間中の1店舗あたりの件数（${d.json.stores}店）` : '期間中の件数'}</span></header>
          <div className="sd-body"><CountChart d={d} max={scale.count} measureKinds={measureKinds} perStore={d.json.stores > 1} msg={msg} /></div>
        </div>
      </div>
    </div>
  );
}

// 右は左との差も出す。順位は小さいほど良いので「左 − 右」が正なら▲。
function Kpis({ d, other, outOfRank }) {
  const k = d.kpi; const o = other && other.kpi;
  const tile = (label, value, diff) => (
    <div className="sc-kpi" key={label}>
      <div className="sc-kpi-l">{label}</div>
      <div className="sc-kpi-v">{value}</div>
      <div className="sc-kpi-d">{o ? <>左より {diff}</> : ' '}</div>
    </div>
  );
  return (
    <div className="sc-kpis">
      {tile('KW1の順位', rankText(k.kw1, outOfRank), o && <DeltaRank value={isNum(o.kw1) && isNum(k.kw1) ? o.kw1 - k.kw1 : null} />)}
      {tile('平均順位', rankText(k.avg, outOfRank), o && <DeltaRank value={isNum(o.avg) && isNum(k.avg) ? o.avg - k.avg : null} />)}
      {tile('UU数（日平均）', fx(k.uu), o && <DeltaPct value={pct(o.uu, k.uu)} />)}
      {tile('アクション数（日平均）', fx(k.act, 2), o && <DeltaPct value={pct(o.act, k.act)} />)}
      {tile('アクション率', isNum(k.rate) ? `${fx(k.rate, 2)}%` : '—', o && <DeltaPct value={isNum(o.rate) && isNum(k.rate) ? k.rate - o.rate : null} unit="pt" />)}
    </div>
  );
}

// ---- グラフ（SVG） ----------------------------------------------------------
// 幅は枠に合わせて測る。viewBox を固定すると、枠が広いとき文字まで拡大されてしまう。

function useWidth() {
  const ref = useRef(null);
  const [w, setW] = useState(520);
  useLayoutEffect(() => {
    const measure = () => { const el = ref.current; if (el && el.clientWidth) setW(Math.max(320, Math.round(el.clientWidth))); };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  return [ref, w];
}

function xAxis(d, X, y) {
  const n = d.dates.length;
  const step = [1, 2, 7, 14, 28, 61, 92, 183].find((s) => n / s <= 6) || 183;
  const out = [];
  for (let i = 0; i < n; i += step) {
    const s = d.dates[i];
    const lab = i === 0 ? `${s.slice(0, 4)}/${md(s)}` : md(s);
    out.push(<text key={i} x={X(i)} y={y} textAnchor="middle">{lab}</text>);
  }
  return out;
}

function linePath(arr, X, Y) {
  let p = ''; let pen = false;
  arr.forEach((v, i) => {
    if (!isNum(v)) { pen = false; return; }
    p += `${pen ? 'L' : 'M'}${X(i).toFixed(1)} ${Y(v).toFixed(1)}`;
    pen = true;
  });
  return p;
}

// 日ごとの透明な四角に吹き出し（title）を付ける。ブラウザ自身の当たり判定に任せる。
function hoverRects(d, X, top, h, text) {
  const n = d.dates.length;
  const stp = n > 1 ? X(1) - X(0) : 20;
  return d.dates.map((s, i) => (
    <rect key={s} x={X(i) - stp / 2} y={top} width={stp} height={h} fill="transparent"><title>{text(i)}</title></rect>
  ));
}

function frame(W, d) {
  const ml = 50; const mr = 70;
  const n = d.dates.length;
  const X = (i) => ml + (n > 1 ? (i / (n - 1)) * (W - ml - mr) : (W - ml - mr) / 2);
  return { ml, mr, X, x1: W - mr };
}

// データが無いときにグラフの真ん中に出す文字（軸と目盛りはそのまま描く）
function emptyText(msg, x, y) {
  return msg ? <text x={x} y={y} textAnchor="middle" className="sc-empty-t">{msg}</text> : null;
}

function endLabel(arr, X, Y, text, color, dy = 0) {
  let i = arr.length - 1;
  while (i >= 0 && !isNum(arr[i])) i -= 1;
  if (i < 0) return null;
  return <text x={X(i) + 6} y={Y(arr[i]) + 4 + dy} className="sd-t-ink2" style={{ fill: color, fontWeight: 700 }}>{text(arr[i])}</text>;
}

function RankChart({ d, outOfRank, msg }) {
  const [ref, W] = useWidth();
  const { ml, X, x1 } = frame(W, d);
  const t = 10; const h = 150; const H = t + h + 26;
  const Y = (r) => t + h * Math.sqrt((Math.min(Math.max(r, 1), outOfRank) - 1) / (outOfRank - 1));
  const lastK = d.ma.kw1.filter(isNum).pop();
  const lastA = d.ma.kwavg.filter(isNum).pop();
  const apart = isNum(lastK) && isNum(lastA) && Math.abs(Y(lastK) - Y(lastA)) < 12;
  return (
    <div ref={ref}>
      <svg className="sd-chart" width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="順位の推移">
        {[1, 3, 5, 10, 15, outOfRank].map((r) => (
          <g key={r}>
            <line className="sd-grid" x1={ml} x2={x1} y1={Y(r)} y2={Y(r)} />
            <text x={ml - 6} y={Y(r) + 4} textAnchor="end">{r === outOfRank ? '圏外' : `${r}位`}</text>
          </g>
        ))}
        <path d={linePath(d.ma.kwavg, X, Y)} fill="none" stroke={AVG_COLOR} strokeWidth="1.8" strokeLinejoin="round" />
        <path d={linePath(d.ma.kw1, X, Y)} fill="none" stroke={KW1_COLOR} strokeWidth="2.4" strokeLinejoin="round" />
        {endLabel(d.ma.kwavg, X, Y, (v) => `平均 ${rankText(v, outOfRank)}`, AVG_COLOR, apart && lastA >= lastK ? 7 : 0)}
        {endLabel(d.ma.kw1, X, Y, (v) => `KW1 ${rankText(v, outOfRank)}`, KW1_COLOR, apart && lastA < lastK ? 7 : apart ? -7 : 0)}
        {xAxis(d, X, H - 6)}
        {emptyText(msg, (ml + x1) / 2, t + h / 2)}
        {hoverRects(d, X, t, h, (i) => `${dayLabel(d.dates[i])}\nKW1 ${rankText(d.ma.kw1[i], outOfRank)}\n平均 ${rankText(d.ma.kwavg[i], outOfRank)}`)}
      </svg>
    </div>
  );
}

// markerRow: 左右どちらかに対策の丸があるときは、両方に同じ高さを取って下のグラフの位置をそろえる
function UuChart({ d, max, measureKinds, markerRow, msg }) {
  const [ref, W] = useWidth();
  const { ml, X, x1 } = frame(W, d);
  const markers = d.json.markers || [];
  const t = 10; const h = 140; const my = t + h + 18; const H = (markerRow ? my + 14 : t + h) + 26;
  const Y = (v) => t + h * (1 - v / max);
  const colorOf = Object.fromEntries(measureKinds.map((k) => [k.label, k.color]));
  const idx = Object.fromEntries(d.dates.map((s, i) => [s, i]));
  const fmt = (v) => (max < 10 ? fx(v, 1) : fmtN(v));
  return (
    <div ref={ref}>
      <svg className="sd-chart" width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="UU数の推移">
        {[0, max / 2, max].map((v) => (
          <g key={v}>
            <line className="sd-grid" x1={ml} x2={x1} y1={Y(v)} y2={Y(v)} />
            <text x={ml - 6} y={Y(v) + 4} textAnchor="end">{fmt(v)}</text>
          </g>
        ))}
        <path d={linePath(d.ma.uu, X, Y)} fill="none" stroke="#343a40" strokeWidth="2" strokeLinejoin="round" />
        {endLabel(d.ma.uu, X, Y, (v) => fx(v, 1), '#343a40')}
        {emptyText(msg, (ml + x1) / 2, t + h / 2)}
        {hoverRects(d, X, t, h, (i) => `${dayLabel(d.dates[i])}\nUU数（7日平均） ${fx(d.ma.uu[i], 1)}\nUU数（その日） ${fx(d.s.uu[i], 1)}`)}
        {markers.length > 0 && <text x={0} y={my + 4} className="sd-t-ink2" style={{ fontWeight: 700 }}>対策</text>}
        {markers.filter((m) => idx[m.date] !== undefined).map((m, j) => (
          <circle key={`${m.date}-${m.kind}-${j}`} cx={X(idx[m.date])} cy={my} r="5.5" fill={colorOf[m.kind] || '#868e96'} stroke="#fff" strokeWidth="1.5">
            <title>{`${ymd(m.date)} ${m.kind}\n${m.text}`}</title>
          </circle>
        ))}
        {xAxis(d, X, H - 6)}
      </svg>
    </div>
  );
}

function WeekChart({ d, max, msg }) {
  const [ref, W] = useWidth();
  const ml = 50; const mr = 16; const x0 = ml; const x1 = W - mr;
  const t = 10; const h = 130; const bot = t + h; const H = bot + 26;
  const Y = (v) => bot - h * (v / max);
  const bwid = (x1 - x0) / Math.max(d.weeks.length, 1);
  const fmt = (v) => (max < 10 ? fx(v, 1) : fmtN(v));
  const labelEvery = Math.ceil(d.weeks.length / 8);
  return (
    <div ref={ref}>
      <svg className="sd-chart" width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="アクションの内訳（週ごと）">
        {[0, max / 2, max].map((v) => (
          <g key={v}>
            <line className="sd-grid" x1={x0} x2={x1} y1={Y(v)} y2={Y(v)} />
            <text x={x0 - 6} y={Y(v) + 4} textAnchor="end">{fmt(v)}</text>
          </g>
        ))}
        {d.weeks.map((w, j) => {
          const x = x0 + j * bwid + 2; const bw = Math.max(bwid - 4, 2);
          let y = bot;
          const bars = ACTION_PARTS.map(([k, , c]) => {
            const hh = h * (w[k] / max);
            if (hh <= 0.5) return null;
            y -= hh;
            return <rect key={k} x={x} y={y + 1} width={bw} height={Math.max(hh - 2, 0.5)} rx="2" fill={c} />;
          });
          const range = `${md(d.dates[w.a])}〜${md(d.dates[w.b])}`;
          return (
            <g key={w.a}>
              {bars}
              <rect x={x} y={t} width={bw} height={h} fill="transparent">
                <title>{`${range}\n電話 ${fx(w.call, 1)}\n経路検索 ${fx(w.route, 1)}\nサイト ${fx(w.site, 1)}`}</title>
              </rect>
              {j % labelEvery === 0 && <text x={x + bw / 2} y={H - 6} textAnchor="middle">{md(d.dates[w.a])}</text>}
            </g>
          );
        })}
        {emptyText(msg, (x0 + x1) / 2, t + h / 2)}
      </svg>
    </div>
  );
}

// msg（読み込み中・読めなかった）のときは件数を「—」にする
function CountChart({ d, max, measureKinds, perStore, msg }) {
  const [ref, W] = useWidth();
  const rowH = 24; const H = measureKinds.length * rowH + 8; const ml = 96; const mr = 70; const x0 = ml; const x1 = W - mr;
  return (
    <div ref={ref}>
      <svg className="sd-chart" width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="対策の件数">
        {measureKinds.map((k, j) => {
          const v = d.counts[k.label] || 0; const y = 4 + j * rowH; const w = (x1 - x0) * (v / max);
          return (
            <g key={k.label}>
              <text x={ml - 8} y={y + 14} textAnchor="end" className="sd-t-ink2">{k.label}</text>
              <rect x={x0} y={y + 4} width={Math.max(w, 0)} height="13" rx="3" fill={k.color} />
              <text x={x0 + w + 6} y={y + 15} className="sd-t-ink2">{msg ? '—' : `${perStore ? fx(v, 2) : fmtN(v)}件`}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
